/**
 * Виджет «Реестр инфраструктурных проектов» (FR-3): таблица «строка =
 * объект» с фильтрами (вид работ / статус / АБП / способ закупки),
 * поиском по названию (FR-3.2), раскрытием строки в договоры объекта
 * (FR-3.3) и открытием карточки проекта кликом по строке (FR-3.4).
 * Пустой реестр — список с подписью, без ошибки (FR-3.5).
 */
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fmt1 } from './format';
import { StatusPill } from './StatusPill';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import type { InfrastructureProject } from '../../../types/projects';

const ALL = 'all';

/** Виды работ фильтра — фиксированный справочник подраздела (FR-4.1). */
const WORK_KEYS = ['bip', 'smr', 'psd', 'supervision', 'events'];
const STATUS_KEYS = ['inProgress', 'done', 'paused'];
const PROCUREMENT_KEYS = ['tender', 'single'];

interface ProjectsRegistryProps {
  projects: InfrastructureProject[];
  onOpen: (project: InfrastructureProject) => void;
}

interface FilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  allLabel: string;
  options: Array<{ value: string; label: string }>;
  testId: string;
}

const FilterSelect = ({
  value,
  onChange,
  allLabel,
  options,
  testId,
}: FilterSelectProps) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      className="h-9 w-auto min-w-36 gap-2 text-sm"
      data-testid={testId}
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={ALL}>{allLabel}</SelectItem>
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const ProjectsRegistry = ({ projects, onOpen }: ProjectsRegistryProps) => {
  const { t } = useTranslation();
  const [work, setWork] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [abp, setAbp] = useState(ALL);
  const [procurement, setProcurement] = useState(ALL);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // Опции АБП — из данных реестра (справочника заказчиков на фронте нет).
  const abpOptions = [...new Set(projects.flatMap((p) => p.abp ?? []))];

  const filtered = projects.filter((project) => {
    if (work !== ALL && project.work !== work) return false;
    if (status !== ALL && project.status !== status) return false;
    if (abp !== ALL && project.abp !== abp) return false;
    if (procurement !== ALL && project.procurement !== procurement) {
      return false;
    }
    const q = query.trim().toLowerCase();
    if (q && !project.name.toLowerCase().includes(q)) return false;
    return true;
  });

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const workLabel = (key: string): string =>
    t(`projects.infrastructure.works.${key}`, { defaultValue: key });
  const procurementLabel = (key: string | null): string =>
    key === null
      ? '—'
      : t(`projects.infrastructure.procurementNames.${key}`, {
          defaultValue: key,
        });

  return (
    <Card className="shadow-card" data-testid="projects-registry">
      <CardHeader className="flex-row flex-wrap items-baseline gap-x-3 gap-y-1 space-y-0">
        <CardTitle>{t('projects.infrastructure.registry.title')}</CardTitle>
        <span className="text-xs text-text-faint">
          {t('projects.infrastructure.registry.hint')}
        </span>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2" data-testid="projects-registry-filters">
          <FilterSelect
            value={work}
            onChange={setWork}
            allLabel={t('projects.infrastructure.registry.workAll')}
            options={WORK_KEYS.map((key) => ({
              value: key,
              label: workLabel(key),
            }))}
            testId="projects-filter-work"
          />
          <FilterSelect
            value={status}
            onChange={setStatus}
            allLabel={t('projects.infrastructure.registry.statusAll')}
            options={STATUS_KEYS.map((key) => ({
              value: key,
              label: t(`projects.infrastructure.statuses.${key}`),
            }))}
            testId="projects-filter-status"
          />
          <FilterSelect
            value={abp}
            onChange={setAbp}
            allLabel={t('projects.infrastructure.registry.abpAll')}
            options={abpOptions.map((name) => ({ value: name, label: name }))}
            testId="projects-filter-abp"
          />
          <FilterSelect
            value={procurement}
            onChange={setProcurement}
            allLabel={t('projects.infrastructure.registry.procurementAll')}
            options={PROCUREMENT_KEYS.map((key) => ({
              value: key,
              label: procurementLabel(key),
            }))}
            testId="projects-filter-procurement"
          />
          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('projects.infrastructure.registry.searchPlaceholder')}
            className="h-9 min-w-44 flex-1 text-sm"
            data-testid="projects-registry-search"
          />
        </div>

        {filtered.length === 0 ? (
          // FR-3.5: пустой реестр — подпись, без ошибки и без каркаса таблицы.
          <p
            className="py-8 text-center text-sm text-text-muted"
            data-testid="projects-registry-empty"
          >
            {t('projects.infrastructure.registry.empty')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-[54rem]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>
                    {t('projects.infrastructure.registry.name')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('projects.infrastructure.registry.sum')}
                  </TableHead>
                  <TableHead>
                    {t('projects.infrastructure.registry.period')}
                  </TableHead>
                  <TableHead>{t('projects.infrastructure.registry.abp')}</TableHead>
                  <TableHead>
                    {t('projects.infrastructure.registry.procurement')}
                  </TableHead>
                  <TableHead>
                    {t('projects.infrastructure.registry.status')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((project) => {
                  const isOpen = expanded.has(project.id);
                  return [
                    <TableRow
                      key={project.id}
                      tabIndex={0}
                      className="cursor-pointer hover:bg-brand-50/60 focus-visible:bg-brand-50/60"
                      onClick={() => onOpen(project)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') onOpen(project);
                      }}
                      data-testid={`projects-registry-row-${project.id}`}
                    >
                      <TableCell className="py-2 pr-0">
                        {project.contracts.length > 0 && (
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            aria-label={t(
                              'projects.infrastructure.registry.toggleContracts',
                            )}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleExpanded(project.id);
                            }}
                            className="rounded p-1 text-text-faint hover:bg-surface-muted hover:text-text"
                            data-testid={`projects-registry-toggle-${project.id}`}
                          >
                            <ChevronRight
                              className={cn(
                                'size-4 transition-transform',
                                isOpen && 'rotate-90',
                              )}
                            />
                          </button>
                        )}
                      </TableCell>
                      <TableCell className="max-w-96">
                        <span className="font-semibold leading-snug">
                          {project.name}
                        </span>
                        <span className="mt-0.5 block text-xs text-text-muted">
                          {[project.territory, workLabel(project.work)]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {project.sumMln != null ? fmt1(project.sumMln) : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {project.period ?? '—'}
                      </TableCell>
                      <TableCell className="text-text-muted">
                        {project.abp ?? '—'}
                      </TableCell>
                      <TableCell className="text-text-muted">
                        {procurementLabel(project.procurement)}
                      </TableCell>
                      <TableCell>
                        <StatusPill status={project.status} />
                      </TableCell>
                    </TableRow>,
                    // FR-3.3: раскрытие строки — договоры объекта.
                    isOpen ? (
                      <TableRow
                        key={`${project.id}-contracts`}
                        className="bg-surface-page/70 hover:bg-surface-page/70"
                        data-testid={`projects-registry-contracts-${project.id}`}
                      >
                        <TableCell colSpan={7} className="py-0">
                          <ul>
                            {project.contracts.map((contract) => (
                              <li
                                key={contract.name}
                                className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-dashed border-surface-border py-2 text-sm last:border-b-0"
                              >
                                <span className="w-24 shrink-0">
                                  <span className="rounded bg-brand-50 px-1.5 py-0.5 text-xs font-semibold text-brand-700">
                                    {workLabel(contract.type)}
                                  </span>
                                </span>
                                <span className="min-w-0 flex-1 text-text-muted">
                                  {contract.name}
                                </span>
                                <span className="whitespace-nowrap font-semibold tabular-nums">
                                  {contract.sumMln != null
                                    ? `${fmt1(contract.sumMln)} ${t('projects.units.mlnTg')}`
                                    : '—'}
                                </span>
                                <span className="w-36 shrink-0 whitespace-nowrap text-text-muted">
                                  {procurementLabel(contract.procurement)}
                                </span>
                                <span className="w-28 shrink-0 whitespace-nowrap text-right tabular-nums text-text-muted">
                                  {contract.developmentPct != null
                                    ? t(
                                        'projects.infrastructure.registry.development',
                                        { pct: contract.developmentPct },
                                      )
                                    : '—'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </TableCell>
                      </TableRow>
                    ) : null,
                  ];
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <p
          className="border-t border-surface-border pt-2.5 text-xs text-text-faint"
          data-testid="projects-registry-count"
        >
          {t('projects.infrastructure.registry.shown', {
            shown: filtered.length,
            total: projects.length,
          })}
        </p>
      </CardContent>
    </Card>
  );
};
